import { Role } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Accordion } from '@/components/basicComponents/accordion';
import { Banner } from '@/components/basicComponents/banner';
import CheckBox from '@/components/basicComponents/checkbox';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Input, { ErrorText } from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Label from '@/components/basicComponents/label';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { ThreeColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { getBrowserTimezone } from '@/shared/utils/date';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloError, ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { COUNTRY_CALLING_CODES_MAP, createUserSchema, EmailNotificationFrequency, ROLE, updateUserSchema } from '@trace-8/trace8-schema';
import _ from 'lodash';
import { FormEvent, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { usersPageTabs } from '..';
import { CREATE_USER_MUTATION, LIST_ADVISORY_FIRM_USERS_QUERY, LIST_PLATFORM_MANAGER_USERS_QUERY, UPDATE_USER_MUTATION } from '../queries';
import { IUser } from '../types';

interface IForm {
  id?: string;
  email: string;
  emailNotifications: EmailNotificationFrequency;
  firstName: string;
  lastName: string;
  officeNumber: {
    countryCode: string;
    number: string;
  };
  roles: ROLE[];
  supervisorId: string;
  tenantId: string;
  timezone: string;
  title: string;
}

const defaultFormValues: IForm = {
  email: '',
  emailNotifications: EmailNotificationFrequency.None,
  firstName: '',
  lastName: '',
  officeNumber: null,
  roles: [ROLE.VIEWER],
  supervisorId: null,
  tenantId: '',
  timezone: '',
  title: ''
};

const countryCodeSelectOptions = Object.entries(COUNTRY_CALLING_CODES_MAP).map(([key, value]) => ({ label: value, value: key }));

interface ModifyUserModalPageProps {
  tenantId: string;
  user: IUser | null;
  activeTab: string;
  backToTitle: string;
  openModalWindow: (modalName: string, user: IUser) => void;
  refetch: () => Promise<ApolloQueryResult<any>>;
  handleClose: () => void;
}

export const ModifyUserModalPage = ({ tenantId, user, activeTab, backToTitle, openModalWindow, refetch, handleClose }: ModifyUserModalPageProps) => {
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();

  const { user: currentUser } = useAuth();
  const [zodSchema, setZodSchema] = useState(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, dirtyFields }
  } = useForm<IForm>({ mode: 'onChange', defaultValues: defaultFormValues, resolver: zodSchema ? zodResolver(zodSchema) : null });

  const [loading, setLoading] = useState(false);
  const [allTimezonesSelectOptions] = useState<ISelectValue[]>(() =>
    Intl.supportedValuesOf('timeZone').map((timezone) => createSimpleSelectOption(timezone))
  );
  const rolesFilter = [
    ROLE.PRIMARY_ADMIN,
    ROLE.ADVISOR_TEAM_DEFAULT,
    ROLE.INVESTMENT_TEAM_DEFAULT,
    ROLE.OPERATIONS_TEAM_DEFAULT,
    ROLE.COMPLIANCE_TEAM_DEFAULT,
    ...(!currentUser?.roles.includes(ROLE.SUPER_ADMIN) ? [ROLE.SUPER_ADMIN] : [])
  ];
  const [chunkedRoles] = useState<ROLE[][]>(() =>
    _.chunk(
      Object.values(ROLE)
        .sort((a, b) => a.localeCompare(b))
        .filter((role) => !rolesFilter.includes(role)),
      4
    )
  );
  const [selectOptions, setSelectOptions] = useState<ISelectValue[]>([]);

  const [createUser] = useMutation(CREATE_USER_MUTATION);
  const [updateUser] = useMutation(UPDATE_USER_MUTATION);

  const { loading: listPlatformManagerUsersLoading } = useQuery<{
    listPlatformManagerUsers: { total: number; page: number; perPage: number; data: IUser[] };
  }>(LIST_PLATFORM_MANAGER_USERS_QUERY, {
    skip: activeTab !== usersPageTabs[0].value || !tenantId,
    variables: {
      filters: {
        limit: 1000,
        page: 1
      }
    },
    onCompleted: ({ listPlatformManagerUsers }) => {
      setSelectOptions(
        listPlatformManagerUsers.data
          .filter((userItem) => userItem.id !== user?.id)
          .map((userItem) => createSimpleSelectOption(userItem.id, `${userItem.firstName} ${userItem.lastName}`))
      );
    }
  });

  const { loading: listAdvisoryFirmUsersLoading } = useQuery<{
    listAdvisoryFirmUsers: { total: number; page: number; perPage: number; data: IUser[] };
  }>(LIST_ADVISORY_FIRM_USERS_QUERY, {
    skip: activeTab !== usersPageTabs[1].value || !tenantId,
    variables: {
      filters: {
        tenantId: tenantId,
        limit: 1000,
        page: 1
      }
    },
    onCompleted: ({ listAdvisoryFirmUsers }) => {
      setSelectOptions(
        listAdvisoryFirmUsers.data
          .filter((userItem) => userItem.id !== user?.id)
          .map((userItem) => createSimpleSelectOption(userItem.id, `${userItem.firstName} ${userItem.lastName}`))
      );
    }
  });

  useEffect(() => {
    if (!user) {
      setZodSchema(createUserSchema);
      reset((formValues) => ({ ...formValues, tenantId, timezone: getBrowserTimezone() }));
      return;
    }

    setZodSchema(updateUserSchema);
    reset((formValues) => ({
      ...formValues,
      id: user.id,
      tenantId,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      title: user?.title ?? '',
      roles: user.roles.map((role) => ROLE[role]),
      timezone: user.timezone,
      initials: user.initials,
      officeNumber: user.officePhoneNumber,
      supervisorId: user.supervisorId,
      emailNotifications: user?.emailNotifications ? EmailNotificationFrequency[user.emailNotifications] : EmailNotificationFrequency.None
    }));
  }, [user, tenantId]);

  const prepareFormDataForSubmission = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { officeNumber } = getValues();

    if (!officeNumber?.countryCode && !officeNumber?.number) {
      setValue('officeNumber', null);
    }

    handleSubmit(onSubmit, (error) => {
      console.log(error);
    })();
  };

  const onSubmit = async (data: IForm) => {
    try {
      setErrorMessage(null);
      setLoading(true);

      const { id, email, emailNotifications, firstName, lastName, officeNumber, roles, tenantId, timezone, title, supervisorId } = data;

      if (user) {
        await updateUser({
          variables: {
            data: {
              id,
              tenantId,
              ...(dirtyFields?.email && { email }),
              ...(dirtyFields?.emailNotifications && {
                emailNotifications: getEnumKeyByValue(EmailNotificationFrequency, emailNotifications) as EmailNotificationFrequency
              }),
              ...(dirtyFields?.firstName && { firstName }),
              ...(dirtyFields?.lastName && { lastName }),
              ...(dirtyFields?.timezone && { timezone }),
              ...(dirtyFields?.roles && { roles: roles.map((role) => getEnumKeyByValue(ROLE, role) as Role) }),
              ...(dirtyFields?.officeNumber && { officeNumber: officeNumber }),
              ...(dirtyFields?.title && { title }),
              ...(dirtyFields?.supervisorId && { supervisorId })
            }
          }
        });

        await refetch();
        handleClose();
        return;
      }

      const { data: createUserData } = await createUser({
        variables: {
          data: {
            tenantId,
            firstName,
            lastName,
            email,
            timezone,
            emailNotifications: getEnumKeyByValue(EmailNotificationFrequency, emailNotifications) as EmailNotificationFrequency,
            roles: roles.map((role) => getEnumKeyByValue(ROLE, role) as Role),
            ...(dirtyFields?.officeNumber && { officeNumber: officeNumber }),
            ...(dirtyFields?.title && { title }),
            ...(dirtyFields?.supervisorId && { supervisorId })
          }
        }
      });

      await refetch();
      handleClose();
      openModalWindow('send-welcome-email', createUserData.createUser as IUser);
    } catch (err) {
      const error = err as ApolloError;
      console.error(error);
      if (error?.message === 'This Email Is Already In Use') {
        setErrorMessage('Email already in use');
        return;
      }
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <ModalWrappedContent
          isOpen={loading}
          title="Saving"
          showCancelButton={false}
          showCloseIcon={false}
          isUseOutsideClick={false}
          onClose={() => {}}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle={backToTitle} />} />
        <PageTitle title={user ? 'Editing User' : 'Add New User'} />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <PaddingWrap>
          <ModifyUserForm id="modifyUserForm" onSubmit={prepareFormDataForSubmission}>
            <Accordion title="User Details">
              <SectionBody>
                <ThreeColumnWrapper isMobile={isMobile}>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        label="First Name"
                        value={value || ''}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        errorText={error?.message}
                        isRequiredField
                        size="md"
                      />
                    )}
                  />
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        label="Last Name"
                        value={value || ''}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        errorText={error?.message}
                        isRequiredField
                        size="md"
                      />
                    )}
                  />
                  <Controller
                    name="title"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        label="Title"
                        value={value || ''}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        errorText={error?.message}
                        size="md"
                      />
                    )}
                  />
                </ThreeColumnWrapper>
                <ThreeColumnWrapper isMobile={isMobile}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        label="Email"
                        value={value || ''}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        errorText={error?.message}
                        styleType={!user || (user && user?.lastLogin === 'Never') ? 'default' : 'disabled'}
                        isRequiredField={!user || (user && user?.lastLogin === 'Never')}
                        size="md"
                      />
                    )}
                  />
                  <PhoneFields>
                    <Select
                      label="Country Code"
                      value={user?.mobilePhoneNumber?.countryCode ?? ''}
                      options={countryCodeSelectOptions}
                      size="md"
                      width="200px"
                      styleType="disabled"
                    />
                    <Input
                      label="Mobile"
                      value={user?.mobilePhoneNumber?.number ?? ''}
                      size="md"
                      styleType="disabled"
                      tooltipWidth={300}
                      tooltip="When Two Factor Authentication is enabled the user will be prompted to add a mobile number when logging in for the first time."
                    />
                  </PhoneFields>
                  <PhoneFields>
                    <Controller
                      name="officeNumber.countryCode"
                      control={control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Select
                          label="Country Code"
                          value={value || ''}
                          setValue={onChange}
                          options={countryCodeSelectOptions}
                          errorText={error?.message}
                          size="md"
                          width="200px"
                        />
                      )}
                    />
                    <Controller
                      name="officeNumber.number"
                      control={control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          label="Other Phone"
                          value={value || ''}
                          onChange={(e) => {
                            onChange(e.target.value);
                          }}
                          errorText={error?.message}
                          size="md"
                        />
                      )}
                    />
                  </PhoneFields>
                </ThreeColumnWrapper>
                <ThreeColumnWrapper isMobile={isMobile}>
                  <Controller
                    name="emailNotifications"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        label="Email Notifications"
                        value={value || ''}
                        options={Object.entries(EmailNotificationFrequency).map(([key, value]) => ({ label: value, value: key }))}
                        setValue={onChange}
                        errorText={error?.message}
                        size="md"
                      />
                    )}
                  />
                  <Controller
                    name="timezone"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        label="Timezone"
                        value={value || ''}
                        options={allTimezonesSelectOptions}
                        setValue={onChange}
                        errorText={error?.message}
                        withSearch
                        isRequiredField
                        size="md"
                      />
                    )}
                  />
                  <Controller
                    name="supervisorId"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        label="Reports To:"
                        value={value || ''}
                        options={selectOptions}
                        loading={listAdvisoryFirmUsersLoading || listPlatformManagerUsersLoading}
                        setValue={onChange}
                        errorText={error?.message}
                        size="md"
                      />
                    )}
                  />
                </ThreeColumnWrapper>
                <Controller
                  name="roles"
                  control={control}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <RolesWrapper>
                      <TeamRolesLabel
                        size="md"
                        tooltipWidth={300}
                        tooltip="User roles allow for assignment to relevant tasks and restrict edit rights throughout the platform.  To give this user the most expansive privileges, include the Admin role along with any relevant functional roles."
                      >
                        Team Roles
                      </TeamRolesLabel>
                      {error?.message && <ErrorText size="md">{error.message}</ErrorText>}
                      <ThreeColumnWrapper isMobile={isMobile}>
                        {chunkedRoles.map((roles, index) => (
                          <RolesColumn key={index}>
                            {roles.map((role) => {
                              // Viewer role will always be enabled as a minimum for all users and cannot be removed
                              if (role === ROLE.VIEWER) {
                                return (
                                  <CheckboxWrapper key={role} id={role} disabled={true}>
                                    <CheckBox id={role} isActive={true} disabled={true} />
                                    {role}
                                  </CheckboxWrapper>
                                );
                              }
                              return (
                                <CheckboxWrapper
                                  key={role}
                                  id={role}
                                  onClick={() => {
                                    const updatedEligibility = value.includes(role) ? value.filter((el) => el !== role) : [...value, role];
                                    onChange(updatedEligibility);
                                  }}
                                >
                                  <CheckBox id={role} isActive={value.includes(role)} />
                                  {role}
                                </CheckboxWrapper>
                              );
                            })}
                          </RolesColumn>
                        ))}
                      </ThreeColumnWrapper>
                    </RolesWrapper>
                  )}
                />
              </SectionBody>
            </Accordion>
            <FormFooter onCancel={handleClose} disableSaveButton={!!Object.keys(errors || {}).length || loading} />
          </ModifyUserForm>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const ModifyUserForm = styled.form`
  margin: 50px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const PhoneFields = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const RolesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TeamRolesLabel = styled(Label)`
  font-weight: 700;
  margin-top: 5px;
`;

const RolesColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const CheckboxWrapper = styled.div<{ disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  display: flex;
  gap: 9px;
  color: ${themeVar('font.base')};
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  padding: 10px;
`;
